import React, { useState } from 'react';
import { X, CreditCard, Plane, Shield, Hotel, FileText, Lock, Eye, Download } from 'lucide-react';

interface WalletDocument {
  id: string;
  title: string;
  category: 'boarding-pass' | 'id-passport' | 'hotel' | 'insurance' | 'payment' | 'other';
  brand?: string;
  brandColor?: string;
  content: string;
  isProtected: boolean;
  created_at: string;
  confirmationNumber?: string;
  flightNumber?: string;
}

interface DigitalWalletPanelProps {
  documents: WalletDocument[];
  onClose: () => void;
  onUnlock: (docId: string, password: string) => Promise<boolean>;
  onPreview: (doc: WalletDocument) => void;
  onDownload: (doc: WalletDocument) => void;
}

const CATEGORY_CONFIG = {
  'boarding-pass': {
    icon: Plane,
    color: 'from-blue-600 to-blue-800',
    label: 'Boarding Pass',
    emoji: '✈️'
  },
  'id-passport': {
    icon: Shield,
    color: 'from-red-600 to-red-800',
    label: 'ID / Passport',
    emoji: '🛂'
  },
  'hotel': {
    icon: Hotel,
    color: 'from-purple-600 to-purple-800',
    label: 'Hotel',
    emoji: '🏨'
  },
  'insurance': {
    icon: Shield,
    color: 'from-green-600 to-green-800',
    label: 'Insurance',
    emoji: '🛡️'
  },
  'payment': {
    icon: CreditCard,
    color: 'from-amber-600 to-amber-800',
    label: 'Payment Card',
    emoji: '💳'
  },
  'other': {
    icon: FileText,
    color: 'from-slate-600 to-slate-800',
    label: 'Document',
    emoji: '📄'
  }
};

export const DigitalWalletPanel: React.FC<DigitalWalletPanelProps> = ({
  documents,
  onClose,
  onUnlock,
  onPreview,
  onDownload
}) => {
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [unlockingCard, setUnlockingCard] = useState<string | null>(null);
  const [password, setPassword] = useState('');

  const handleUnlock = async (docId: string) => {
    if (!password) return;
    const success = await onUnlock(docId, password);
    if (success) {
      setUnlockingCard(null);
      setPassword('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl max-h-[90vh] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl shadow-2xl overflow-hidden border-2 border-amber-400/30">
        
        {/* Header - Gold WHIPT WALLET branding */}
        <div className="relative bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 p-6 text-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute top-4 right-4 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
          
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-100 via-white to-amber-100 tracking-wider drop-shadow-lg">
            WHIPT WALLET
          </h1>
          <p className="text-amber-100 text-sm mt-2 font-light tracking-widest">
            YOUR SECURE TRAVEL DOCUMENTS
          </p>
        </div>

        {/* Cards Grid */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {documents.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">💳</div>
              <p className="text-slate-400 text-lg">No documents in your wallet yet</p>
              <p className="text-slate-500 text-sm mt-2">Upload encrypted documents to the Travel tab</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {documents.map((doc) => {
                const config = CATEGORY_CONFIG[doc.category];
                const Icon = config.icon;
                const isExpanded = selectedCard === doc.id;
                const cardColor = doc.brandColor || config.color;
                const displayName = doc.brand || doc.title;

                return (
                  <div
                    key={doc.id}
                    className={`relative rounded-2xl bg-gradient-to-br ${cardColor} p-6 shadow-xl transition-all duration-300 cursor-pointer hover:scale-105 hover:shadow-2xl ${
                      isExpanded ? 'md:col-span-2 scale-105' : ''
                    }`}
                    onClick={() => setSelectedCard(isExpanded ? null : doc.id)}
                  >
                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-2xl">
                          {config.emoji}
                        </div>
                        <div>
                          <p className="text-xs text-white/70 uppercase tracking-widest font-light">
                            {config.label}
                          </p>
                          <p className="text-white font-bold text-lg mt-1">
                            {displayName.length > 30 && !isExpanded 
                              ? displayName.substring(0, 30) + '...' 
                              : displayName}
                          </p>
                          {/* Show confirmation/flight number if available */}
                          {(doc.flightNumber || doc.confirmationNumber) && (
                            <div className="mt-2 space-y-1">
                              {doc.flightNumber && (
                                <p className="text-xs text-white/90 font-mono bg-white/10 px-2 py-1 rounded inline-block">
                                  ✈️ {doc.flightNumber}
                                </p>
                              )}
                              {doc.confirmationNumber && (
                                <p className="text-xs text-white/90 font-mono bg-white/10 px-2 py-1 rounded inline-block ml-2">
                                  🎫 {doc.confirmationNumber}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {doc.isProtected && (
                        <Lock className="w-5 h-5 text-amber-300" />
                      )}
                    </div>

                    {/* Expanded Actions */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-white/20">
                        {doc.isProtected && unlockingCard !== doc.id ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setUnlockingCard(doc.id);
                            }}
                            className="w-full py-3 px-4 rounded-xl bg-white/20 hover:bg-white/30 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                          >
                            <Lock className="w-4 h-4" />
                            Unlock to View
                          </button>
                        ) : doc.isProtected && unlockingCard === doc.id ? (
                          <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              placeholder="Enter password"
                              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/30 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-400"
                              onKeyDown={(e) => e.key === 'Enter' && handleUnlock(doc.id)}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleUnlock(doc.id)}
                                className="flex-1 py-2 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors"
                              >
                                Unlock
                              </button>
                              <button
                                onClick={() => {
                                  setUnlockingCard(null);
                                  setPassword('');
                                }}
                                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onPreview(doc);
                              }}
                              className="flex-1 py-3 px-4 rounded-xl bg-white/20 hover:bg-white/30 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              Preview
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDownload(doc);
                              }}
                              className="flex-1 py-3 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </button>
                          </div>
                        )}
                        <p className="text-xs text-white/60 text-center mt-3">
                          Added {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}

                    {/* Gold accent bar at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-900/50 px-6 py-4 border-t border-amber-400/20 text-center">
          <p className="text-xs text-slate-400">
            🔒 All documents are encrypted with AES-256-GCM
          </p>
        </div>
      </div>
    </div>
  );
};
